import { useEffect } from "react";
import { SITE_NAME, SITE_DEFAULT_DESCRIPTION } from "@/lib/site-meta";

const DEFAULT_DESCRIPTION = SITE_DEFAULT_DESCRIPTION;
const DEFAULT_IMAGE = "/opengraph.jpg";

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const JSON_LD_ID = "json-ld-main";

interface MetaOptions {
  description?: string | null;
  /**
   * Image URL. Accepts either a root-relative path (e.g. "/opengraph.jpg")
   * or an absolute URL (e.g. an avatar CDN URL). Absolute URLs are used as-is;
   * relative paths are prefixed with window.location.origin.
   */
  image?: string | null;
  /**
   * og:type value. Defaults to "website". Pass "profile" for profile pages.
   */
  type?: string;
  /**
   * Pre-serialized JSON-LD structured data string. Pass JSON.stringify(...) from
   * the caller so the value is a stable primitive in React's dependency comparison.
   * Pass null to remove any previously injected JSON-LD script.
   */
  jsonLd?: string | null;
}

export function useDocumentTitle(title?: string | null, meta?: MetaOptions) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const description = meta?.description ?? DEFAULT_DESCRIPTION;

    const rawImage = meta?.image;
    const image = rawImage
      ? rawImage.startsWith("http")
        ? rawImage
        : `${window.location.origin}${rawImage}`
      : `${window.location.origin}${DEFAULT_IMAGE}`;

    const url = window.location.origin + window.location.pathname;
    const ogType = meta?.type ?? "website";

    document.title = fullTitle;

    setMetaProperty("og:title", fullTitle);
    setMetaProperty("og:description", description);
    setMetaProperty("og:url", url);
    setMetaProperty("og:image", image);
    setMetaProperty("og:type", ogType);
    setMetaProperty("og:site_name", SITE_NAME);

    setMetaName("twitter:card", "summary_large_image");
    setMetaName("twitter:title", fullTitle);
    setMetaName("twitter:description", description);
    setMetaName("twitter:image", image);

    const jsonLdStr = meta?.jsonLd ?? null;
    let ldScript = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (jsonLdStr) {
      if (!ldScript) {
        ldScript = document.createElement("script");
        ldScript.id = JSON_LD_ID;
        ldScript.type = "application/ld+json";
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = jsonLdStr;
    } else {
      ldScript?.remove();
    }

    return () => {
      document.title = SITE_NAME;
      setMetaProperty("og:title", SITE_NAME);
      setMetaProperty("og:description", DEFAULT_DESCRIPTION);
      setMetaProperty("og:url", window.location.origin);
      setMetaProperty("og:image", `${window.location.origin}${DEFAULT_IMAGE}`);
      setMetaProperty("og:type", "website");
      setMetaName("twitter:title", SITE_NAME);
      setMetaName("twitter:description", DEFAULT_DESCRIPTION);
      setMetaName("twitter:image", `${window.location.origin}${DEFAULT_IMAGE}`);
      document.getElementById(JSON_LD_ID)?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, meta?.description, meta?.image, meta?.type, meta?.jsonLd]);
}
